/**
 * One dataset's metadata, as a form (M6 §4; client, 2026-09-18).
 *
 * Lifted out of `EditDatasetModal` when the Update Metadata rail button arrived: the same
 * fields are now both a dialog (from the Datasets tab) and a panel (from the rail),
 * and two copies of a form whose field names are the catalogue's schema is two copies to
 * keep in step with the schema.
 *
 * `idPrefix` is what keeps the two instances apart in the DOM *and* what keeps the dialog's
 * test ids exactly what they were: the modal passes `mgr-edit`, so every `id` and
 * `data-testid` below still reads `mgr-edit-title`, `mgr-edit-domain`, and so on.
 *
 * The form is controlled from outside — this file holds no state — because the dialog fills
 * it in twice (the row, then the record) and the panel fills it once, and the rules about
 * which fill wins belong with the thing doing the filling.
 */
import { useTranslation } from 'react-i18next';

import type { DatasetClassification, DatasetPatch, Domain } from '../../lib/api';
import { LicenceInfo } from './LicenceInfo';
import { CADENCES, CLASSIFICATIONS, LICENCES } from './manager';

export interface DatasetMetadataFormProps {
  form: DatasetPatch;
  domains: Domain[];
  onChange: (patch: Partial<DatasetPatch>) => void;
  /** `mgr-edit` in the dialog, `mgr-meta` in the rail panel. */
  idPrefix: string;
}

export function DatasetMetadataForm({
  form,
  domains,
  onChange,
  idPrefix,
}: DatasetMetadataFormProps) {
  const { t } = useTranslation();
  const id = (name: string) => `${idPrefix}-${name}`;

  return (
    <>
      <div className="mgr-form">
        <label className="mgr-field" htmlFor={id('title')}>
          <span className="mgr-label">{t('manager.f.title')}</span>
          <input
            id={id('title')}
            data-testid={id('title')}
            className="mgr-input"
            value={form.title}
            onChange={(e) => onChange({ title: e.target.value })}
          />
        </label>

        <label className="mgr-field" htmlFor={id('domain')}>
          <span className="mgr-label">{t('manager.f.domain')}</span>
          <select
            id={id('domain')}
            data-testid={id('domain')}
            className="mgr-input"
            value={form.domain ?? ''}
            onChange={(e) => onChange({ domain: e.target.value || null })}
          >
            <option value="">{t('manager.f.choose')}</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>

        <label className="mgr-field" htmlFor={id('source')}>
          <span className="mgr-label">{t('manager.f.source')}</span>
          <input
            id={id('source')}
            data-testid={id('source')}
            className="mgr-input"
            value={form.source_org}
            onChange={(e) => onChange({ source_org: e.target.value })}
          />
        </label>

        <label className="mgr-field" htmlFor={id('licence')}>
          <span className="mgr-label">{t('manager.f.licence')} <LicenceInfo /></span>
          <select
            id={id('licence')}
            data-testid={id('licence')}
            className="mgr-input"
            value={form.licence ?? ''}
            onChange={(e) => onChange({ licence: e.target.value || null })}
          >
            <option value="">{t('manager.f.choose')}</option>
            {/* A licence the catalogue already carries but the wizard does not offer
                (a harvested dataset's own terms) stays selectable rather than being
                silently rewritten to "Other" by opening this form. */}
            {(LICENCES as readonly string[]).includes(form.licence ?? '')
              ? null
              : form.licence
                ? <option value={form.licence}>{form.licence}</option>
                : null}
            {LICENCES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>

        <label className="mgr-field" htmlFor={id('cadence')}>
          <span className="mgr-label">{t('manager.f.cadence')}</span>
          <select
            id={id('cadence')}
            data-testid={id('cadence')}
            className="mgr-input"
            value={form.cadence ?? ''}
            onChange={(e) => onChange({ cadence: e.target.value || null })}
          >
            <option value="">{t('manager.f.choose')}</option>
            {CADENCES.map((c) => (
              <option key={c} value={c}>
                {t(`manager.cadence.${c}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="mgr-field mgr-wide" htmlFor={id('abstract')}>
          <span className="mgr-label">{t('manager.f.abstract')}</span>
          <textarea
            id={id('abstract')}
            data-testid={id('abstract')}
            className="mgr-input mgr-textarea"
            rows={3}
            value={form.abstract}
            onChange={(e) => onChange({ abstract: e.target.value })}
          />
        </label>

        <label className="mgr-field mgr-wide" htmlFor={id('lineage')}>
          <span className="mgr-label">{t('manager.f.lineage')}</span>
          <textarea
            id={id('lineage')}
            data-testid={id('lineage')}
            className="mgr-input mgr-textarea"
            rows={2}
            value={form.lineage ?? ''}
            onChange={(e) => onChange({ lineage: e.target.value || null })}
          />
        </label>

        <label className="mgr-field" htmlFor={id('source_detail')}>
          <span className="mgr-label">{t('manager.f.sourceDetail')}</span>
          <input
            id={id('source_detail')}
            data-testid={id('source_detail')}
            className="mgr-input"
            value={form.source_detail ?? ''}
            onChange={(e) => onChange({ source_detail: e.target.value || null })}
          />
        </label>

        <label className="mgr-field" htmlFor={id('capture_method')}>
          <span className="mgr-label">{t('manager.f.captureMethod')}</span>
          <input
            id={id('capture_method')}
            data-testid={id('capture_method')}
            className="mgr-input"
            value={form.capture_method ?? ''}
            onChange={(e) => onChange({ capture_method: e.target.value || null })}
          />
        </label>

        <label className="mgr-field mgr-wide" htmlFor={id('accuracy')}>
          <span className="mgr-label">{t('manager.f.accuracy')}</span>
          <textarea
            id={id('accuracy')}
            data-testid={id('accuracy')}
            className="mgr-input mgr-textarea"
            rows={2}
            value={form.accuracy ?? ''}
            onChange={(e) => onChange({ accuracy: e.target.value || null })}
          />
        </label>

        <label className="mgr-field" htmlFor={id('resolution')}>
          <span className="mgr-label">{t('manager.f.resolution')}</span>
          <input
            id={id('resolution')}
            data-testid={id('resolution')}
            className="mgr-input"
            value={form.resolution ?? ''}
            onChange={(e) => onChange({ resolution: e.target.value || null })}
          />
        </label>

        <label className="mgr-field" htmlFor={id('contact')}>
          <span className="mgr-label">{t('manager.f.contact')}</span>
          <input
            id={id('contact')}
            data-testid={id('contact')}
            className="mgr-input"
            value={form.contact ?? ''}
            onChange={(e) => onChange({ contact: e.target.value || null })}
          />
        </label>
      </div>

      <fieldset className="mgr-cls" data-testid={id('classification')}>
        <legend className="mgr-label">{t('manager.f.classification')}</legend>
        <div className="mgr-cls-cards">
          {CLASSIFICATIONS.map((cls: DatasetClassification) => (
            <label
              key={cls}
              className={
                form.classification === cls ? `mgr-cls-card on ${cls}` : `mgr-cls-card ${cls}`
              }
              htmlFor={id(`classification-${cls}`)}
            >
              <input
                type="radio"
                id={id(`classification-${cls}`)}
                data-testid={id(`classification-${cls}`)}
                name={id('classification')}
                value={cls}
                checked={form.classification === cls}
                onChange={() => onChange({ classification: cls })}
              />
              <span className="mgr-cls-name">{t(`manager.cls.${cls}`)}</span>
              <span className="mgr-cls-blurb">{t(`manager.cls.${cls}Blurb`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="mgr-field" htmlFor={id('websiteVisible')}>
        <span className="mgr-label">{t('manager.f.websiteVisible')}</span>
        <select
          id={id('websiteVisible')}
          data-testid={id('websiteVisible')}
          className="mgr-input"
          value={form.website_visible ? 'yes' : 'no'}
          onChange={(e) => onChange({ website_visible: e.target.value === 'yes' })}
        >
          <option value="yes">{t('manager.visible.yes')}</option>
          <option value="no">{t('manager.visible.no')}</option>
        </select>
      </label>
    </>
  );
}

export default DatasetMetadataForm;
